import { postSelskabsblanket } from "@/api/selskabsblanket";
import type { CompanyRegistrationSubmitPayload } from "@blanket/pages/registration/form/registrationFormUtils";

// Kun opslag i CVR går direkte til selskabsblanket-api's APIM-host. Indsendelsen gør ikke
// længere — se sendBlanket nedenfor (ADV-297).
const COMPANY_NAME_LOOKUP_ENDPOINT = `${import.meta.env.VITE_SELSKABSBLANKETTEN_API_BASE_URL}/LookupCompany`;

const apimSubscriptionKey = import.meta.env
  .VITE_SELSKABSBLANKETTEN_APIM_SUBSCRIPTION_KEY;

const blanketApiHeaders = (
  headers: Record<string, string>,
): Record<string, string> => ({
  ...headers,
  ...(apimSubscriptionKey
    ? { "Ocp-Apim-Subscription-Key": apimSubscriptionKey }
    : {}),
});
export interface CompanyNameLookupItem {
  companyName: string | null;
  cvr: number;
  pNumbers: number[] | null;
}

export type CompanyNameLookupResult = CompanyNameLookupItem[];

export const normalizePNumbers = (value: unknown): number[] => {
  if (value == null) return [];
  if (Array.isArray(value)) {
    return [
      ...new Set(
        value.map((entry) => Number(entry)).filter((n) => Number.isFinite(n)),
      ),
    ];
  }
  const single = Number(value);
  return Number.isFinite(single) ? [single] : [];
};

const normalizeLookupItem = (raw: unknown): CompanyNameLookupItem | null => {
  if (!raw || typeof raw !== "object") return null;
  const record = raw as Record<string, unknown>;
  const cvr = Number(record.cvr ?? record.Cvr);
  if (!Number.isFinite(cvr)) return null;

  const pNumbers = normalizePNumbers(record.pNumbers ?? record.PNumbers);

  return {
    companyName: (record.companyName ?? record.CompanyName ?? null) as
      string | null,
    cvr,
    pNumbers: pNumbers.length ? pNumbers : null,
  };
};

export const parseCompanyNameLookupResult = (
  raw: unknown,
): CompanyNameLookupResult => {
  if (!Array.isArray(raw)) return [];

  const items = raw
    .map(normalizeLookupItem)
    .filter((item): item is CompanyNameLookupItem => item != null);

  const merged = new Map<string, CompanyNameLookupItem>();

  for (const item of items) {
    const key = `${item.cvr}:${(item.companyName ?? "").trim().toLowerCase()}`;
    const existing = merged.get(key);
    const combined = [
      ...new Set([...(existing?.pNumbers ?? []), ...(item.pNumbers ?? [])]),
    ];

    merged.set(key, {
      companyName: item.companyName ?? existing?.companyName ?? null,
      cvr: item.cvr,
      pNumbers: combined.length ? combined : null,
    });
  }

  return [...merged.values()];
};

export const lookupCompanyName = async (
  name: string,
  signal?: AbortSignal,
): Promise<CompanyNameLookupResult> => {
  const url = new URL(COMPANY_NAME_LOOKUP_ENDPOINT);
  url.searchParams.set("name", name);

  const response = await fetch(url.toString(), {
    method: "GET",
    headers: blanketApiHeaders({
      Accept: "application/json",
    }),
    signal,
  });
  if (!response.ok) {
    const message = await response.text().catch(() => "");
    throw new Error(
      message || `Company name lookup failed (HTTP ${response.status}).`,
    );
  }

  return parseCompanyNameLookupResult(await response.json());
};

/**
 * Indsender blanketten gennem portalens FrontendAPI på den eksisterende cookie-session
 * (ADV-297), som stempler AIKA-brugerens navn og brugeridentifikator på blanketten — plus
 * fuldmagtshaverens navn under fuldmagt — og videresender til selskabsblanket-api.
 *
 * Tidligere gik den herfra direkte til den anonyme SendBlanket med et almindeligt `fetch` uden
 * credentials. Portalen er cookie-autentificeret, så der var intet token at sende med, og
 * modtageren af PDF'en kunne derfor ikke se hvem der havde indsendt.
 */
export const sendBlanket = async (
  payload: CompanyRegistrationSubmitPayload,
): Promise<void> => {
  await postSelskabsblanket(payload);
};
